import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useClerkAuth';
import { useToast } from '@/hooks/use-toast';
import TopNavBar from '@/components/TopNavBar';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  BookOpen,
  Edit2,
  Trash2,
  Eye,
  Search,
  FileText,
  Loader2,
  Plus,
  ArrowLeft,
} from 'lucide-react';
import { Article } from '@/types/article';
import { fetchArticles, deleteArticle } from '@/services/articles';

const ArticleManager = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { canEditArticle, canDeleteArticle } = useAuth();

  const [articles, setArticles] = useState<Article[]>([]);
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState<string | null>(null);

  const loadArticles = useCallback(async () => {
    setIsLoading(true);
    try {
      const fetchedArticles = await fetchArticles();
      setArticles(fetchedArticles);
    } catch (error) {
      console.error('Error loading articles:', error);
      toast({
        title: t('roleManager.error'),
        description: t('articleManager.failedToLoad'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast, t]);

  useEffect(() => {
    if (!canEditArticle()) {
      navigate('/dashboard');
      return;
    }
    loadArticles();
  }, [canEditArticle, navigate, loadArticles]);

  useEffect(() => {
    // Filter articles based on search term
    if (searchTerm.trim() === '') {
      setFilteredArticles(articles);
    } else {
      const filtered = articles.filter(
        article =>
          article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (article.description &&
            article.description
              .toLowerCase()
              .includes(searchTerm.toLowerCase()))
      );
      setFilteredArticles(filtered);
    }
  }, [searchTerm, articles]);

  const handleDeleteArticle = async (articleId: string) => {
    if (!canDeleteArticle()) {
      toast({
        title: t('articleManager.permissionDenied'),
        description: t('articleManager.permissionDenied'),
        variant: 'destructive',
      });
      return;
    }

    try {
      await deleteArticle(articleId);
      setArticles(prev => prev.filter(a => a.id !== articleId));
      toast({
        title: t('articleManager.articleDeleted'),
        description: t('articleManager.articleDeletedDesc'),
      });
    } catch (error) {
      console.error('Error deleting article:', error);
      toast({
        title: t('roleManager.error'),
        description: t('articleManager.failedToDelete'),
        variant: 'destructive',
      });
    } finally {
      setShowDeleteConfirm(false);
      setArticleToDelete(null);
    }
  };

  const getArticleStats = (article: Article) => {
    const verbCount = article.sentences.reduce(
      (count, sentence) => count + (sentence.verbs?.length || 0),
      0
    );
    return {
      sentenceCount: article.sentences.length,
      verbCount,
      hasImage: !!article.imageUrl,
    };
  };

  return (
    <>
      <TopNavBar
        currentPage="articles"
        onNavigateToAdmin={() => navigate('/?view=admin')}
        onNavigateToArticles={() => navigate('/')}
        onNavigateToDashboard={() => navigate('/dashboard')}
        onNavigateToQuestions={() => navigate('/questions')}
      />
      <div className="min-h-screen bg-gradient-to-br from-background to-learning-muted/20 p-6">
        <div className="container max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-6">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="bg-white/10 border-white/20"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('articleManager.backToDashboard')}
              </Button>
            </div>

            <div className="bg-gradient-hero rounded-lg p-6 text-primary-foreground mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold mb-2">
                    {t('articleManager.title')}
                  </h1>
                  <p className="text-primary-foreground/90">
                    {t('articleManager.subtitle')}
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/create-article')}
                  className="bg-white/20 text-white hover:bg-white/30"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('articleManager.createNewArticle')}
                </Button>
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          <Card className="mb-6 bg-white/95 backdrop-blur-sm border-0 shadow-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="h-5 w-5" />
                {t('articleManager.searchArticles')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                <div className="flex-1">
                  <Input
                    placeholder={t('articleManager.searchPlaceholder')}
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="bg-white"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Articles List */}
          <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                {t('articleManager.articlesCount', {
                  count: filteredArticles.length,
                })}
              </CardTitle>
              <CardDescription>
                {searchTerm
                  ? t('articleManager.showingResults', { query: searchTerm })
                  : t('articleManager.allArticles')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin" />
                  <span className="ml-2">
                    {t('articleManager.loadingArticles')}
                  </span>
                </div>
              ) : filteredArticles.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  {searchTerm ? (
                    <div>
                      <p className="mb-2">
                        {t('articleManager.noArticlesFound', {
                          query: searchTerm,
                        })}
                      </p>
                      <Button
                        variant="outline"
                        onClick={() => setSearchTerm('')}
                      >
                        {t('articleManager.clearSearch')}
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <p className="mb-4">
                        {t('articleManager.noArticlesYet')}
                      </p>
                      <Button onClick={() => navigate('/create-article')}>
                        <Plus className="h-4 w-4 mr-2" />
                        {t('articleManager.createArticle')}
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredArticles.map(article => {
                    const stats = getArticleStats(article);
                    return (
                      <div
                        key={article.id}
                        className="group border border-border/50 rounded-lg p-4 hover:shadow-lg transition-all duration-300 bg-white"
                      >
                        {/* Article Image */}
                        {article.imageUrl ? (
                          <img
                            src={article.imageUrl}
                            alt={article.title}
                            className="w-full h-40 object-cover rounded-lg mb-4"
                          />
                        ) : (
                          <div className="w-full h-40 bg-gradient-to-br from-learning-muted/20 to-learning-muted/40 rounded-lg mb-4 flex items-center justify-center">
                            <BookOpen className="h-8 w-8 text-muted-foreground" />
                          </div>
                        )}

                        {/* Article Info */}
                        <div className="space-y-3">
                          <h3 className="font-semibold text-lg line-clamp-2 group-hover:text-learning-primary transition-colors">
                            {article.title}
                          </h3>

                          {article.description && (
                            <p className="text-sm text-muted-foreground line-clamp-3">
                              {article.description}
                            </p>
                          )}

                          {/* Stats */}
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <FileText className="h-3 w-3" />
                              {stats.sentenceCount}{' '}
                              {t('articleManager.sentences')}
                            </span>
                            <span className="flex items-center gap-1">
                              <BookOpen className="h-3 w-3" />
                              {stats.verbCount} {t('articleManager.verbs')}
                            </span>
                            <span>
                              {new Date(article.createdAt).toLocaleDateString()}
                            </span>
                          </div>

                          {/* Actions */}
                          <div className="flex gap-2 pt-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                navigate(`/edit-article/${article.id}`)
                              }
                              className="flex-1"
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              {t('articleManager.viewEdit')}
                            </Button>

                            {canEditArticle() && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  navigate(`/articles/${article.id}/manage`)
                                }
                              >
                                <Edit2 className="h-3 w-3" />
                              </Button>
                            )}

                            {canDeleteArticle() && (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => {
                                  setArticleToDelete(article.id);
                                  setShowDeleteConfirm(true);
                                }}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Delete Confirmation Dialog */}
          <ConfirmDialog
            open={showDeleteConfirm}
            onOpenChange={setShowDeleteConfirm}
            title={t('articleManager.deleteArticle')}
            description={t('articleManager.deleteConfirmation')}
            onConfirm={() =>
              articleToDelete && handleDeleteArticle(articleToDelete)
            }
          />
        </div>
      </div>
    </>
  );
};

export default ArticleManager;
